/**
 * Routing panel — task, then model; pins, allowed providers, fallback order.
 *
 * One engine request: the routing read (task mappings plus the router's
 * option list with its advisory data-policy column). Difficulty tiers,
 * per-task editing, allowed-provider sets and fallback ordering are not in
 * the engine's read today, so those controls are visible, greyed out and
 * marked "Coming soon" — never pretending to work.
 */

import React, { useMemo } from 'react';
import { getLLMTaskMappings, type LLMTaskMappingsResponse } from '@/api/llmSettings';
import { ComingSoonBlock, RouterReadError, RouterReadLoading } from './PanelErrorBoundary';
import { useRouterRead } from './useRouterRead';

export const ROUTING_SWR_KEY = '/api/v1/settings/llm/routing';

export const RoutingPanel: React.FC = () => {
  const routing = useRouterRead<LLMTaskMappingsResponse>(ROUTING_SWR_KEY, (signal) =>
    getLLMTaskMappings({ signal }),
  );

  const optionLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of routing.data?.options ?? []) {
      map.set(`${option.provider_id}::${option.model_id}`, option.label);
    }
    return map;
  }, [routing.data]);

  if (routing.error) {
    return <RouterReadError error={routing.error} onRetry={routing.retry} />;
  }
  if (routing.isLoading) {
    return <RouterReadLoading label="Loading the routing table…" />;
  }

  const tasks = routing.data?.tasks ?? [];
  const options = routing.data?.options ?? [];

  return (
    <div data-testid="models-routing-routing-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-3)' }}>
      {tasks.length === 0 ? (
        <div className="sp-empty" style={{ padding: '24px 12px' }}>
          The engine reports no task mappings.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tasks.map((task) => {
            const target = optionLabels.get(`${task.provider_id}::${task.model_id}`) ?? task.model_id;
            return (
              <div
                key={task.action_type}
                data-testid={`mr-route-row-${task.action_type}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  alignItems: 'center',
                  gap: 'var(--sp-space-3)',
                  border: '1px solid var(--sp-border)',
                  borderRadius: 'var(--sp-radius-lg)',
                  background: 'var(--sp-bg-elev)',
                  padding: '10px var(--sp-space-3)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{task.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>{task.description}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--sp-text)' }}>{target}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>{task.provider_id}</div>
                </div>
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: task.is_override ? 'var(--sp-accent)' : 'var(--sp-text-dim)',
                    border: '1px solid var(--sp-border)',
                    borderRadius: 999,
                    padding: '2px 8px',
                  }}
                >
                  {task.is_override ? 'Pinned' : 'Default'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <ComingSoonBlock
        title="Per-task pins, difficulty tiers, allowed providers and fallback order"
        body="The engine serves these mappings read-only on this surface today. Editing pins, routing by difficulty, restricting providers per task and ordering fallbacks land with the scored router."
        testId="mr-routing-edit-coming-soon"
      />

      <details
        style={{
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          padding: 'var(--sp-space-3)',
        }}
      >
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--sp-text-muted)' }}>
          Model options and advisory data terms ({options.length})
        </summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          {options.map((option) => (
            <div
              key={`${option.provider_id}::${option.model_id}`}
              data-testid={`mr-option-row-${option.model_id}`}
              style={{
                border: '1px solid var(--sp-border)',
                borderRadius: 'var(--sp-radius-lg)',
                background: 'var(--sp-bg-elev)',
                padding: '8px var(--sp-space-3)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{option.label}</span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                  {option.provider_id} · {option.quality_tier}
                  {option.requires_api_key ? ' · key required' : ''}
                </span>
              </div>
              {option.data_policy ? (
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--sp-text-muted)' }}>
                  {option.data_policy.keeps}; {option.data_policy.trains}; {option.data_policy.region}.
                  {option.data_policy.checked_on ? ` Checked ${option.data_policy.checked_on}.` : ''}
                  {option.data_policy.staleness_marker ? ` (${option.data_policy.staleness_marker})` : ''}
                  <span className="mono" style={{ fontSize: 9, color: 'var(--sp-text-dim)', marginLeft: 6 }}>
                    {option.data_policy.label}
                  </span>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};
